import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Ranking } from '../../types';
import { rankingsApi } from '../../api';

interface RankingsState {
  rankings: Ranking[];
  currentRanking: Ranking | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: RankingsState = {
  rankings: [],
  currentRanking: null,
  status: 'idle',
  error: null,
};

export const fetchRankings = createAsyncThunk(
  'rankings/fetchRankings',
  async () => {
    const response = await rankingsApi.getAll();
    return response.data;
  }
);

export const fetchRankingById = createAsyncThunk(
  'rankings/fetchRankingById',
  async (id: string) => {
    const response = await rankingsApi.getById(id);
    return response.data;
  }
);

export const createRanking = createAsyncThunk(
  'rankings/createRanking',
  async (ranking: { title: string; description: string; coverUrl: string; creatorId?: string }) => {
    const backendRanking = {
      title: ranking.title,
      description: ranking.description,
      coverUrl: ranking.coverUrl,
      creatorId: ranking.creatorId || '127.0.0.1'
    };
    
    const response = await rankingsApi.create(backendRanking);
    return response.data;
  }
);

export const updateRanking = createAsyncThunk(
  'rankings/updateRanking',
  async ({ id, ranking }: { id: string; ranking: { title: string; description: string; coverUrl: string; type?: string; tags?: string[] } }) => {
    // 添加 creatorId 字段
    const updatedRanking = {
      ...ranking,
      creatorId: '127.0.0.1',
    };
    
    const response = await rankingsApi.update(id, updatedRanking);
    return response.data;
  }
);

export const deleteRanking = createAsyncThunk(
  'rankings/deleteRanking',
  async (id: string) => {
    await rankingsApi.delete(id);
    return id;
  }
);

const rankingsSlice = createSlice({
  name: 'rankings',
  initialState,
  reducers: {
    clearCurrentRanking: (state) => {
      state.currentRanking = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRankings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchRankings.fulfilled, (state, action: PayloadAction<Ranking[]>) => {
        state.status = 'succeeded';
        state.rankings = action.payload;
      })
      .addCase(fetchRankings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || '获取排行榜失败';
      })
      .addCase(fetchRankingById.fulfilled, (state, action: PayloadAction<Ranking>) => {
        state.currentRanking = action.payload;
      })
      .addCase(createRanking.fulfilled, (state, action: PayloadAction<Ranking>) => {
        state.rankings.push(action.payload);
      })
      .addCase(updateRanking.fulfilled, (state, action: PayloadAction<Ranking>) => {
        const index = state.rankings.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.rankings[index] = action.payload;
        }
        if (state.currentRanking?.id === action.payload.id) {
          state.currentRanking = action.payload;
        }
      })
      .addCase(deleteRanking.fulfilled, (state, action: PayloadAction<string>) => {
        state.rankings = state.rankings.filter(r => r.id !== Number(action.payload));
        if (state.currentRanking?.id === Number(action.payload)) {
          state.currentRanking = null;
        }
      });
  },
});

export const { clearCurrentRanking } = rankingsSlice.actions;
export default rankingsSlice.reducer; 